import React, { ReactElement } from "react"
import {
  IconButton,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { FaIcon } from "../../../../../mp-core/icons"
import DropdownMenu from "../../DropdownMenu/DropdownMenu"
import { useStyles } from "../styles"
import { Columns, SortTypes } from "../types"
import { getSortIconName, selectSorting } from "../helpers"
import Checkbox from "../../Checkbox"

interface Props {
  headerColumns: Columns[]
  selected: boolean
  sortBy: string
  order: SortTypes
  displayedProperties: string[]
  allDisplayedProperties?: string[]
  disableCheckbox?: boolean
  onSort: (sortBy: string, order: SortTypes) => void
  getRowAction?: (id: string) => ReactElement
  onSelectAllRows?: (selectAll: boolean) => void
  selectDisplayedProperties?: (selectedPropId: string) => void
}

const TableHeadComponent: React.FC<Props> = ({
  headerColumns,
  sortBy: currentSortBy,
  order: currentOrder,
  selected = false,
  displayedProperties,
  allDisplayedProperties,
  disableCheckbox = false,
  onSort,
  getRowAction,
  onSelectAllRows,
  selectDisplayedProperties
}) => {
  const { classes, cx } = useStyles()

  const handleCellSort = (name: string) => {
    const { sortBy, order } = selectSorting(name, currentSortBy, currentOrder)
    onSort(sortBy, order)
  }

  return (
    <TableHead>
      <TableRow
        data-automation-id="data-table-head-row"
        className={classes.tableHeadRow}
      >
        {!disableCheckbox && (
          <TableCell
            component="th"
            scope="col"
            className={cx(
              classes.tableCell,
              classes.headTableCell,
              classes.clickable
            )}
            data-automation-id="data-table-head-checkbox-cell"
          >
            <Checkbox
              checked={selected}
              onChange={e => onSelectAllRows?.(e.target.checked)}
              className={classes.checkbox}
              data-automation-id="data-table-head-checkbox"
            />
          </TableCell>
        )}
        {headerColumns
          .filter(({ propertyId }) => displayedProperties.includes(propertyId))
          .map(({ label, name, sortable }) => (
            <TableCell
              key={name}
              component="th"
              scope="col"
              className={cx(classes.tableCell, classes.headTableCell, {
                [classes.clickable]: sortable
              })}
              onClick={() => sortable && handleCellSort(name)}
              data-automation-id="data-table-head-cell"
            >
              <Stack
                spacing={2}
                direction="row"
                display="flex"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  component="p"
                  data-automation-id="data-table-column-title"
                >
                  {label}
                </Typography>
                {sortable && (
                  <FaIcon
                    solid
                    name={getSortIconName(currentSortBy, currentOrder, name)}
                    className={cx(
                      classes.sortIcon,
                      currentSortBy === name && classes.activeSortIcon
                    )}
                    data-automation-id="data-table-column-sort-icon"
                  />
                )}
              </Stack>
            </TableCell>
          ))}

        {(!!getRowAction || !!allDisplayedProperties) && (
          <TableCell
            component="th"
            scope="col"
            className={cx(classes.tableCell, classes.headTableCell)}
            data-automation-id="data-table-head-menu-cell"
          >
            {allDisplayedProperties && (
              <DropdownMenu
                withCheckbox
                id="allDisplayedProperties"
                dropdownTitle="View Columns"
                anchor={
                  <IconButton data-automation-id="data-table-head-menu-button">
                    <FaIcon solid name="cog" />
                  </IconButton>
                }
                options={headerColumns
                  .filter(({ propertyId }) => propertyId !== "className")
                  .map(({ label, propertyId }) => ({
                    id: propertyId,
                    label: label,
                    selected: displayedProperties.includes(propertyId),
                    action: () => selectDisplayedProperties(propertyId),
                    "data-automation-id": "data-table-head-menu-item"
                  }))}
              />
            )}
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  )
}

export default React.memo(TableHeadComponent)
